import { AlertCircle, ArrowRight, MapPin, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { DashboardFilter } from '@/components/sales/DashboardFilter';
import { IndiaMap } from '@/components/sales/IndiaMap';
import { KpiCard } from '@/components/sales/KpiCard';
import { PendingFilter, pendingPresetRange } from '@/components/sales/PendingFilter';
import { SectionCard } from '@/components/sales/SectionCard';
import { SortableRow, type SortableItem } from '@/components/sales/SortableRow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDashboardMetrics, useDashboardPending, useTargetProgress } from '@/hooks';
import useAuth from '@/hooks/useAuth';
import {
  inr,
  MONTHLY_TARGET,
  presetRange,
  type DashRange,
  YEARLY_TARGET,
} from '@/lib/sales-metrics';
import { errorMessage } from '@/utils/apiError';

export function SalesHomeDashboard() {
  const { user } = useAuth();
  // Admin-set per-employee targets (whole rupees); fall back to the shared
  // defaults when an admin hasn't assigned them yet. Treat 0 (or any
  // non-positive) as unset too, so it can't divide into NaN/Infinity below.
  const monthlyTarget =
    user?.monthlyTarget && user.monthlyTarget > 0 ? user.monthlyTarget : MONTHLY_TARGET;
  const yearlyTarget =
    user?.yearlyTarget && user.yearlyTarget > 0 ? user.yearlyTarget : YEARLY_TARGET;

  const [range, setRange] = useState<DashRange>(() => presetRange('month'));
  const [pendingRange, setPendingRange] = useState<DashRange>(() => pendingPresetRange('1m'));
  const [resetTick, setResetTick] = useState(0);

  // All figures are aggregated server-side (bug 7); the dashboard renders straight
  // from the payload. Pending actions are a separate query keyed by their own
  // range, so the pending-work filter doesn't refetch the whole dashboard.
  const {
    data: metrics,
    isError: metricsError,
    error: metricsErr,
    isPending: metricsPending,
    isFetching: metricsFetching,
    refetch: refetchMetrics,
  } = useDashboardMetrics(range);
  const {
    data: pending = [],
    isError: pendingError,
    error: pendingErr,
    isFetching: pendingFetching,
    refetch: refetchPending,
  } = useDashboardPending(pendingRange);
  // Target progress uses fixed current-period revenue (this month / this year),
  // NOT the dashboard filter, so the target cards stay accurate even when the
  // main dashboard is showing "All Time" or a custom range.
  const {
    data: targetProgress,
    isError: targetError,
    error: targetErr,
    isFetching: targetFetching,
    refetch: refetchTarget,
  } = useTargetProgress();
  const monthRevenue = targetProgress?.monthRevenue ?? 0;
  const yearRevenue = targetProgress?.yearRevenue ?? 0;

  // Surface a failure of ANY dashboard query (metrics / pending / target) with the
  // first available error message, and let the RM retry them all with progress
  // feedback. `firstLoading` is the metrics query's own pending state (true even
  // when a request is paused offline), so the grid is hidden ONLY on the very
  // first load - never on a later error - and a metrics-only failure still shows
  // the target/pending widgets that loaded fine. When metrics errored while prior
  // data is retained, that data is stale, so we say so.
  const dashboardError = metricsError || pendingError || targetError;
  const dashboardErr = metricsErr ?? pendingErr ?? targetErr;
  const firstLoading = metricsPending;
  const anyFetching = metricsFetching || pendingFetching || targetFetching;
  const retryAll = (): void => {
    void refetchMetrics();
    void refetchPending();
    void refetchTarget();
  };
  const totals = metrics?.totals ?? {
    leads: 0,
    clients: 0,
    futureClients: 0,
    revenue: 0,
  };
  const tc = metrics?.tcStatus ?? { confirmed: 0, notConfirmed: 0, notSent: 0 };
  const ls = metrics?.leadStatus ?? { lead: 0, futureClient: 0, client: 0 };
  const payStatus = metrics?.paymentStatus ?? { full: 0, partial: 0, notPaid: 0 };
  const series = metrics?.timeSeries ?? [];
  const products = metrics?.products ?? [];
  const cities = metrics?.cities ?? [];
  const states = metrics?.states ?? [];
  const collection = metrics?.paymentCollection ?? {
    collected: 0,
    expected: 0,
    outstanding: 0,
    collectedPct: 0,
    fullPaidCount: 0,
    fullPaidAmount: 0,
    partialCount: 0,
    partialOwed: 0,
    notPaidCount: 0,
    notSentOwed: 0,
  };

  const totalLeads = totals.leads;
  const totalClients = totals.clients;
  const totalFutureClients = totals.futureClients;
  const totalRevenue = totals.revenue;
  const monthlyAchievedPct = Math.min(100, Math.round((monthRevenue / monthlyTarget) * 100));
  const yearlyAchievedPct = Math.min(100, Math.round((yearRevenue / yearlyTarget) * 100));
  const conversionPct = totalLeads ? Math.round((totalClients / totalLeads) * 100) : 0;

  const tcData = [
    { name: 'Confirmed', value: tc.confirmed, color: 'var(--chart-2)' },
    { name: 'Not Confirmed', value: tc.notConfirmed, color: 'var(--chart-4)' },
    { name: 'Not sent', value: tc.notSent, color: 'var(--chart-5)' },
  ];
  const leadData = [
    { name: 'Lead', value: ls.lead, color: 'var(--chart-3)' },
    { name: 'Future Client', value: ls.futureClient, color: 'var(--chart-1)' },
    { name: 'Client', value: ls.client, color: 'var(--chart-2)' },
  ];
  const payData = [
    { name: 'Full Payment', value: payStatus.full, color: 'var(--chart-2)' },
    { name: 'Partial Payment', value: payStatus.partial, color: 'var(--chart-4)' },
    { name: 'Not Paid', value: payStatus.notPaid, color: 'var(--chart-5)' },
  ];

  const layoutKey = `ak:home-layout:${user?.email ?? user?.id ?? 'default'}`;

  const resetLayout = () => {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(`${layoutKey}:`))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
    setResetTick((t) => t + 1);
  };

  const firstName = (user?.name ?? 'there').split(' ')[0];

  const kpiItems: SortableItem[] = [
    {
      id: 'kpi-leads',
      node: (
        <KpiCard
          className="h-full"
          label="No. of Leads"
          value={totalLeads}
          tone="info"
          delta={`+${totalLeads}`}
        />
      ),
    },
    {
      id: 'kpi-clients',
      node: (
        <KpiCard
          className="h-full"
          label="Clients (Converted)"
          value={totalClients}
          tone="success"
          delta={`${conversionPct}%`}
        />
      ),
    },
    {
      id: 'kpi-future',
      node: (
        <KpiCard
          className="h-full"
          label="Future Clients"
          value={totalFutureClients}
          tone="warning"
        />
      ),
    },
    {
      id: 'kpi-revenue',
      node: (
        <KpiCard
          className="h-full"
          label="Revenue Earned"
          value={inr(totalRevenue)}
          tone="success"
        />
      ),
    },
    {
      id: 'kpi-monthly',
      node: (
        <KpiCard
          className="h-full"
          label="Monthly Target"
          value={inr(monthlyTarget)}
          tone="info"
          delta={`${monthlyAchievedPct}% done`}
        />
      ),
    },
    {
      id: 'kpi-yearly',
      node: (
        <KpiCard
          className="h-full"
          label="Yearly Target"
          value={inr(yearlyTarget)}
          tone="info"
          delta={`${yearlyAchievedPct}% done`}
        />
      ),
    },
  ];

  const targetItems: SortableItem[] = [
    {
      id: 'target-monthly',
      node: (
        <SectionCard title="Monthly Target Progress" className="h-full">
          <div className="space-y-3">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-semibold text-foreground tabular-nums">
                {inr(monthRevenue)}
              </span>
              <span className="text-muted-foreground">of {inr(monthlyTarget)}</span>
            </div>
            <Progress value={monthlyAchievedPct} />
            <p className="text-xs text-muted-foreground">
              {monthlyAchievedPct}% achieved · {inr(Math.max(0, monthlyTarget - monthRevenue))}{' '}
              remaining
            </p>
          </div>
        </SectionCard>
      ),
    },
    {
      id: 'target-yearly',
      node: (
        <SectionCard title="Yearly Target Progress" className="h-full">
          <div className="space-y-3">
            <div className="flex items-baseline justify-between text-sm">
              <span className="font-semibold text-foreground tabular-nums">{inr(yearRevenue)}</span>
              <span className="text-muted-foreground">of {inr(yearlyTarget)}</span>
            </div>
            <Progress value={yearlyAchievedPct} />
            <p className="text-xs text-muted-foreground">
              {yearlyAchievedPct}% achieved · {inr(Math.max(0, yearlyTarget - yearRevenue))}{' '}
              remaining
            </p>
          </div>
        </SectionCard>
      ),
    },
  ];

  const statusItems: SortableItem[] = [
    { id: 'donut-tc', node: <StatusDonut title="T&C Status" data={tcData} /> },
    { id: 'donut-lead', node: <StatusDonut title="Lead Status" data={leadData} /> },
    { id: 'donut-pay', node: <StatusDonut title="Payment Status" data={payData} /> },
  ];

  const chartItems: SortableItem[] = [
    {
      id: 'chart-revenue',
      node: (
        <SectionCard title="Revenue Trend" className="h-full">
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={24} tickMargin={6} />
                <YAxis
                  width={44}
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v: number) => `₹${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip formatter={(v: number) => inr(v)} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--chart-1)"
                  fill="url(#rev)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      ),
    },
    {
      id: 'chart-funnel',
      node: (() => {
        const { collected, expected, outstanding, collectedPct } = collection;
        const rows3 = [
          {
            label: 'Fully paid',
            count: collection.fullPaidCount,
            amount: collection.fullPaidAmount,
            color: 'var(--chart-2)',
          },
          {
            label: 'Partial - balance due',
            count: collection.partialCount,
            amount: collection.partialOwed,
            color: 'var(--chart-4)',
          },
          {
            label: 'Not invoiced yet',
            count: collection.notPaidCount,
            amount: collection.notSentOwed,
            color: 'var(--chart-5)',
          },
        ];
        const maxAmt = Math.max(1, ...rows3.map((r) => r.amount));
        return (
          <SectionCard title="Payment Collection" className="h-full">
            <div className="flex flex-col justify-between gap-4 py-2 sm:h-[260px]">
              <div className="grid grid-cols-1 gap-2 text-center xs:grid-cols-3">
                <div className="min-w-0 rounded-lg border border-border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Collected</div>
                  <div className="mt-0.5 truncate text-sm font-bold tabular-nums text-[var(--chart-2)]">
                    {inr(collected)}
                  </div>
                </div>
                <div className="min-w-0 rounded-lg border border-border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Outstanding</div>
                  <div className="mt-0.5 text-sm font-bold tabular-nums text-warning">
                    {inr(outstanding)}
                  </div>
                </div>
                <div className="min-w-0 rounded-lg border border-border p-2">
                  <div className="text-[10px] uppercase text-muted-foreground">Collection %</div>
                  <div className="mt-0.5 text-sm font-bold tabular-nums text-foreground">
                    {collectedPct}%
                  </div>
                </div>
              </div>
              <div className="space-y-2.5">
                {rows3.map((r) => (
                  <div key={r.label} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">
                        {r.label} <span className="text-muted-foreground">·{r.count}</span>
                      </span>
                      <span className="font-semibold tabular-nums text-foreground">
                        {inr(r.amount)}
                      </span>
                    </div>
                    <div className="h-5 w-full overflow-hidden rounded-md bg-secondary">
                      <div
                        className="h-full rounded-md transition-all"
                        style={{ width: `${(r.amount / maxAmt) * 100}%`, background: r.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Expected billing:{' '}
                <span className="font-semibold text-foreground tabular-nums">{inr(expected)}</span>
              </p>
            </div>
          </SectionCard>
        );
      })(),
    },
  ];

  const PRODUCT_H = 'h-[440px]';
  // Avg Sales Size = net revenue (after discount, before GST) per client.
  const avgDeal = totalClients ? totalRevenue / totalClients : 0;
  // Top Product / State / City are the highest earners - the server orders each by
  // revenue, so the first element is the top by revenue.
  const topProduct = products[0];
  const topState = states[0];
  const topCity = cities[0];
  // Avg Unit Price = the top-earning product's revenue per unit sold.
  const avgUnitPrice = topProduct && topProduct.units ? topProduct.revenue / topProduct.units : 0;
  // T&C Pending = links sent but not yet confirmed by the lead (excludes not-sent).
  const tcPending = tc.notConfirmed;
  const insightStats = [
    {
      label: 'Avg Sales Size',
      value: inr(Math.round(avgDeal)),
      hint: `${totalClients} client${totalClients === 1 ? '' : 's'}`,
    },
    {
      label: 'Avg Unit Price',
      value: inr(Math.round(avgUnitPrice)),
      hint: topProduct ? `${topProduct.name} · ${topProduct.units} units` : 'No sales yet',
    },
    {
      label: 'Top Product',
      value: topProduct?.name ?? '-',
      hint: topProduct ? `${topProduct.units} units · ${inr(topProduct.revenue)}` : 'No sales yet',
    },
    {
      label: 'Top State',
      value: topState?.state ?? '-',
      hint: topState ? `${inr(topState.revenue)} · ${topState.clients} clients` : '-',
    },
    {
      label: 'Top City',
      value: topCity?.city ?? '-',
      hint: topCity ? `${topCity.state} · ${inr(topCity.revenue)}` : '-',
    },
    { label: 'Outstanding', value: inr(collection.outstanding), hint: 'Amount not paid yet' },
    { label: 'T&C Pending', value: String(tcPending), hint: 'Sent, not confirmed' },
    { label: 'Lead → Client', value: `${conversionPct}%`, hint: `${totalClients}/${totalLeads}` },
  ];

  const productItems: SortableItem[] = [
    {
      id: 'sales-insights',
      colSpanClass: 'lg:col-span-2',
      node: (
        <SectionCard
          title="Sales Insights"
          className={`${PRODUCT_H} flex flex-col`}
          contentClassName="flex-1 overflow-auto"
        >
          <div className="grid h-full grid-cols-2 content-start gap-2">
            {insightStats.map((s) => (
              <div key={s.label} className="rounded-lg border border-border bg-card p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {s.label}
                </div>
                <div
                  className="mt-1 truncate text-sm font-bold tabular-nums text-foreground"
                  title={s.value}
                >
                  {s.value}
                </div>
                <div className="mt-0.5 truncate text-[10px] text-muted-foreground" title={s.hint}>
                  {s.hint}
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      ),
    },
    {
      id: 'products-details',
      colSpanClass: 'lg:col-span-3',
      node: (
        <SectionCard
          title="Products Sold - details"
          className={`${PRODUCT_H} flex flex-col`}
          contentClassName="flex-1 overflow-auto"
        >
          <table className="w-full min-w-[460px] text-sm">
            <thead className="sticky top-0 z-10 bg-secondary/80 backdrop-blur">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase">Product</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Units</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Revenue</th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase">Avg Price</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-muted-foreground">
                    No products sold in this range.
                  </td>
                </tr>
              )}
              {products.map((p) => (
                <tr key={p.code} className="border-t border-border">
                  <td className="px-3 py-2 font-medium">{p.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.units}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{inr(p.revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {inr(p.units ? p.revenue / p.units : 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      ),
    },
  ];

  const bottomItems: SortableItem[] = [
    {
      id: 'pending',
      node: (
        <SectionCard
          title="Pending Work - Action needed"
          action={<Badge variant="secondary">{pending.length} items</Badge>}
          className="h-full"
        >
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b pb-3">
            <PendingFilter range={pendingRange} onChange={setPendingRange} />
            <span className="text-[11px] text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{pendingRange.label}</span>
            </span>
          </div>
          {pending.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              You&apos;re all caught up.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {pending.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-col items-start justify-between gap-2 py-2.5 sm:flex-row sm:items-center sm:gap-3"
                >
                  <div className="flex min-w-0 items-start gap-3">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {p.name}{' '}
                        <span className="text-xs font-normal text-muted-foreground">
                          · {p.code}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {p.reason} · {p.ageDays}d old
                      </p>
                    </div>
                  </div>
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="shrink-0 self-end sm:self-auto"
                  >
                    <Link to="/sales-rm/leads">
                      Open
                      <ArrowRight className="ml-1 h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      ),
    },
    {
      id: 'geography',
      node: (
        <SectionCard title="Geography - Leads & Clients" className="h-full">
          <Tabs defaultValue="map">
            <TabsList>
              <TabsTrigger value="map">
                <MapPin className="mr-1 h-3.5 w-3.5" /> Map
              </TabsTrigger>
              <TabsTrigger value="table">Table</TabsTrigger>
            </TabsList>
            <TabsContent value="map" className="mt-3">
              <IndiaMap data={states} />
            </TabsContent>
            <TabsContent value="table" className="mt-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <h4 className="mb-2 text-sm font-semibold">By State</h4>
                  <div className="max-h-[420px] overflow-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/60">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                            State
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Leads
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Clients
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Products
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Revenue
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {states.map((s) => (
                          <tr key={s.state} className="border-t border-border">
                            <td className="px-3 py-2">{s.state}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.leads}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.clients}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{s.products}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{inr(s.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h4 className="mb-2 text-sm font-semibold">By City</h4>
                  <div className="max-h-[420px] overflow-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary/60">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                            City
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold uppercase">
                            State
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Leads
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Clients
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Products
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-semibold uppercase">
                            Revenue
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {cities.map((c) => (
                          <tr key={`${c.city}|${c.state}`} className="border-t border-border">
                            <td className="px-3 py-2">{c.city}</td>
                            <td className="px-3 py-2 text-muted-foreground">{c.state}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{c.leads}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{c.clients}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{c.products}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{inr(c.revenue)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </SectionCard>
      ),
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4 p-0 sm:p-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">My Sales Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Welcome back, <span className="font-semibold text-foreground">{firstName}</span> -
            here&apos;s how you&apos;re tracking.{' '}
            <span className="text-xs">(drag widgets to rearrange)</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={resetLayout} className="gap-1.5">
          <RotateCcw className="h-3.5 w-3.5" /> Reset layout
        </Button>
      </div>

      <DashboardFilter range={range} onChange={setRange} />

      {firstLoading ? (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
        >
          <RotateCcw className="h-4 w-4 shrink-0 animate-spin" />
          Loading your stats...
        </div>
      ) : (
        <>
          {dashboardError && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-danger/30 bg-danger-tint px-4 py-3 text-sm">
              <div role="alert" className="flex items-center gap-2 text-danger">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  Could not load your dashboard stats:{' '}
                  {errorMessage(dashboardErr, 'Please try again in a moment.')}
                  {metrics && metricsError ? ' Showing the last loaded data.' : ''}
                </span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={retryAll}
                isLoading={anyFetching}
                className="gap-1.5"
              >
                {!anyFetching && <RotateCcw className="h-3.5 w-3.5" />}
                Retry
              </Button>
            </div>
          )}

          <SortableRow
            key={`r1-${resetTick}`}
            storageKey={`${layoutKey}:kpis`}
            gridClass="grid-cols-1 xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-6"
            items={kpiItems}
          />
          <SortableRow
            key={`r2-${resetTick}`}
            storageKey={`${layoutKey}:targets`}
            gridClass="grid-cols-1 md:grid-cols-2"
            items={targetItems}
          />
          <SortableRow
            key={`r3-${resetTick}`}
            storageKey={`${layoutKey}:status`}
            gridClass="grid-cols-1 md:grid-cols-3"
            items={statusItems}
          />
          <SortableRow
            key={`r4-${resetTick}`}
            storageKey={`${layoutKey}:charts`}
            gridClass="grid-cols-1 lg:grid-cols-2"
            items={chartItems}
          />
          <SortableRow
            key={`r5-${resetTick}`}
            storageKey={`${layoutKey}:products`}
            gridClass="grid-cols-1 lg:grid-cols-5"
            items={productItems}
          />
          <SortableRow
            key={`r6-${resetTick}`}
            storageKey={`${layoutKey}:bottom`}
            gridClass="grid-cols-1"
            items={bottomItems}
          />
        </>
      )}
    </div>
  );
}

interface DonutDatum {
  name: string;
  value: number;
  color: string;
}

function StatusDonut({ title, data }: { title: string; data: DonutDatum[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <SectionCard title={title} className="h-full">
      <div className="flex flex-col items-stretch gap-4 xs:flex-row xs:items-center">
        <div className="mx-auto h-[140px] w-[140px] shrink-0 sm:h-[160px] sm:w-[160px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={2}>
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="flex-1 space-y-1.5">
          <p className="text-2xl font-extrabold tabular-nums">{total}</p>
          <p className="text-xs text-muted-foreground">Total</p>
          <ul className="space-y-1 pt-1">
            {data.map((d) => (
              <li key={d.name} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full" style={{ background: d.color }} />
                  {d.name}
                </span>
                <span className="font-semibold tabular-nums">{d.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SectionCard>
  );
}
